// DailyTasks.js
import React, { useState } from 'react'; // Removed useEffect
import styled from 'styled-components';
import { APP_CONFIG } from '../config/app';
import { RichCoinIcon } from '../assets/icons/Icons';
import { Icon } from '@iconify/react';
import { useUserData } from '../contexts/UserDataContext';

// ----------------------------
// STYLED COMPONENTS
// ----------------------------

const TasksContainer = styled.div`
  margin: 16px;
  margin-top: 24px;
`;

const TasksHeader = styled.div`
  margin-bottom: 12px;
  text-align: left;
`;

const TasksTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  margin-bottom: 4px;
  color: #000000;
`;

const TasksDescription = styled.p`
  font-size: 14px;
  color: #757575;
  margin: 0;
  font-weight: 400;
`;

const TasksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TaskItem = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
`;

const TaskInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TaskIcon = styled.div`
  background-color: #f0f0f0;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const TaskDetails = styled.div`
  text-align: left;
`;

const TaskName = styled.div`
  font-weight: 600;
  font-size: 16px;
`;

const TaskReward = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #757575;
  font-weight: 500;
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const GoButton = styled(ActionButton)`
  background-color: #f0f0f0;
  color: #000000;
`;

const ClaimButton = styled(ActionButton)`
  background-color: #007bff;
  color: white;
`;

const ClaimedStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #28a745;
  font-weight: 600;
`;

const DailyTasks = () => {
  // Get sponsors from UserDataContext
  const { userData, claimSponsorTask, sponsors } = useUserData();
  const [claimingId, setClaimingId] = useState(null);
  const [visitedSponsors, setVisitedSponsors] = useState(new Set());

  const handleGo = (sponsorUrl, sponsorId) => {
    window.open(sponsorUrl, '_blank', 'noopener,noreferrer');
    setVisitedSponsors(prev => new Set(prev).add(sponsorId));
  };

  const handleClaim = async (sponsorId) => {
    if (!userData?.$id) {
      alert('Could not identify user. Please try again later.');
      return;
    }
    setClaimingId(sponsorId);
    try {
      await claimSponsorTask(userData.$id, sponsorId);
      // On success, the context updates the state, and the component re-renders.
      // No need to show an alert as the UI updates automatically.
    } catch (error) {
      console.error('Claim error:', error);
      alert(error.message || 'Failed to claim reward. Please try again.');
    } finally {
      setClaimingId(null);
    }
  };

  return (
    <TasksContainer>
      <TasksHeader>
        <TasksTitle>Daily tasks</TasksTitle>
        <TasksDescription>Complete tasks to get more ${APP_CONFIG.PRIMARY_COIN_SYMBOL}</TasksDescription>
      </TasksHeader>
      
      <TasksList>
        {sponsors.map((sponsor) => {
          const isClaimed = userData?.claimed_sponsor_rewards?.includes(sponsor.$id);
          const isClaiming = claimingId === sponsor.$id;
          const hasVisited = visitedSponsors.has(sponsor.$id);

          return (
            <TaskItem key={sponsor.$id}>
              <TaskInfo>
                <TaskIcon>
                  {sponsor.iconUrl ? (
                    <img src={sponsor.iconUrl} alt={sponsor.name} />
                  ) : (
                    <Icon icon="ph:link-bold" width="28" height="28" />
                  )}
                </TaskIcon>
                <TaskDetails>
                  <TaskName>{sponsor.name}</TaskName>
                  <TaskReward>
                    <RichCoinIcon width="20" height="20" />
                    +{sponsor.reward.toLocaleString()} ${APP_CONFIG.PRIMARY_COIN_SYMBOL}
                  </TaskReward>
                </TaskDetails>
              </TaskInfo>
              
              <div>
                {isClaimed ? (
                  <ClaimedStatus>
                    <Icon icon="ph:check-circle-fill" width="24" height="24" />
                    Claimed
                  </ClaimedStatus>
                ) : (
                  !hasVisited ? (
                    <GoButton onClick={() => handleGo(sponsor.url, sponsor.$id)}>
                      Join
                    </GoButton>
                  ) : (
                    <ClaimButton onClick={() => handleClaim(sponsor.$id)} disabled={isClaiming}>
                      {isClaiming ? 'Claiming...' : 'Claim'}
                    </ClaimButton>
                  )
                )}
              </div>
            </TaskItem>
          );
        })}
      </TasksList>
    </TasksContainer>
  );
};

export default DailyTasks;
